import { useState, useEffect, useCallback, useContext } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { ChildContext } from '../components/Layout/ParentLayout';
import {
  CalendarDaysIcon, ArrowDownTrayIcon, ClockIcon,
} from '@heroicons/react/24/outline';

const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit', second:'2-digit', hour12:true }) : '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .gl * { box-sizing:border-box; }
  .gl { font-family:'Sora',sans-serif; color:#1a1d2e; animation:glFade .4s ease both; }
  @keyframes glFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .gl-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .gl-title { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .gl-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-ghost { background:#f0f2f7; color:#6b7194; } .btn-ghost:hover { background:#e4e7f0; }
  .btn-sm { padding:6px 11px; font-size:.74rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }
  .stat-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:1rem; margin-bottom:1.4rem; }
  .stat-card { background:white; border-radius:14px; padding:1rem 1.2rem; border:1px solid #e8ebf4; }
  .sc-icon { width:34px; height:34px; border-radius:10px; display:flex; align-items:center; justify-content:center; margin-bottom:.6rem; }
  .sc-icon svg { width:16px; height:16px; stroke-width:1.8; }
  .sc-icon.green { background:#ecfdf5; color:#10c98e; }
  .sc-icon.amber { background:#fffbeb; color:#f59e0b; }
  .sc-label { font-size:.67rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .sc-value { font-size:1.4rem; font-weight:700; color:#0f1123; font-family:'JetBrains Mono',monospace; margin-top:2px; }
  .gl-toolbar { display:flex; gap:.7rem; flex-wrap:wrap; align-items:center; margin-bottom:1.2rem; }
  .gl-date { font-family:'JetBrains Mono',monospace; font-size:.8rem; padding:8px 12px; border:1.5px solid #e0e4f0; border-radius:10px; background:white; outline:none; }
  .gl-date:focus { border-color:#f97316; }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }
  .panel-head { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .panel-sub   { font-size:.7rem; color:#9094a8; margin-top:1px; }
  .tbl { width:100%; border-collapse:collapse; }
  .tbl thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .tbl th { padding:10px 16px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .tbl tbody tr { border-bottom:1px solid #f3f5fb; }
  .tbl tbody tr:last-child { border-bottom:none; }
  .tbl td { padding:10px 16px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .mono { font-family:'JetBrains Mono',monospace; font-size:.76rem; color:#6b7194; }
  .time-badge { display:inline-flex; align-items:center; gap:4px; font-family:'JetBrains Mono',monospace; font-size:.76rem; font-weight:600; padding:3px 9px; border-radius:99px; background:#ecfdf5; color:#10c98e; }
  .time-badge svg { width:11px; height:11px; stroke-width:2; }
  .source-badge { display:inline-flex; font-size:.65rem; font-weight:600; padding:2px 8px; border-radius:99px; }
  .source-badge.rfid   { background:#eef2ff; color:#3b6ef8; }
  .source-badge.manual { background:#fffbeb; color:#f59e0b; }
  .cards { display:none; padding:10px; }
  .card  { background:#fafbff; border-radius:12px; border:1px solid #e8ebf4; padding:13px; margin-bottom:8px; }
  .card-row { display:flex; justify-content:space-between; align-items:center; font-size:.78rem; padding:4px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .card-row:last-of-type { border-bottom:none; }
  .card-lbl { color:#9094a8; font-weight:600; white-space:nowrap; }
  .gl-empty   { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .gl-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner    { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#f97316; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }
  @media (max-width:768px) {
    .stat-grid { grid-template-columns:1fr 1fr; }
    .gl-toolbar { flex-direction:column; align-items:stretch; }
    .tbl-wrap { display:none !important; }
    .cards { display:block; }
  }
`;

export default function ParentGateLog() {
  const { activeChild } = useContext(ChildContext);
  const [date,    setDate]    = useState(new Date().toISOString().slice(0,10));
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchLog = useCallback(async () => {
    if (!activeChild?._id) return;
    try {
      setLoading(true);
      const { data: res } = await api.get(`/parent/gate-log/${activeChild._id}?date=${date}`);
      setData(res);
    } catch { toast.error('Failed to load gate log'); }
    finally { setLoading(false); }
  }, [activeChild, date]);

  useEffect(() => {
    if (activeChild?._id) fetchLog();
    else setData(null);
  }, [activeChild, fetchLog]);

  const exportCSV = () => {
    if (!data?.entries?.length) return toast.error('No data to export');
    const rows = data.entries.map(e => ({
      'Entry Time': fmtTime(e.entryTime),
      'Card UID':   e.cardUID,
      Source:       e.source,
    }));
    const hdrs = Object.keys(rows[0]);
    const csv  = [hdrs.join(','), ...rows.map(r => hdrs.map(h => r[h]).join(','))].join('\n');
    const blob = new Blob([csv], { type:'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `Gate_Log_${activeChild?.name || 'student'}_${date}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const entries    = data?.entries || [];
  const total      = data?.total   || 0;
  const firstEntry = entries[0]?.entryTime ? fmtTime(entries[0].entryTime) : '—';

  return (
    <div className="gl"><style>{style}</style>

      <div className="gl-header">
        <div>
          <div className="gl-title">Gate Log</div>
          <div className="gl-sub">RFID entry log for {activeChild?.name || 'your child'} — when they entered school</div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={exportCSV} disabled={!entries.length}><ArrowDownTrayIcon />Export CSV</button>
      </div>

      {!activeChild ? (
        <div className="gl-empty">Select a child to view their gate log.</div>
      ) : (
        <>
          <div className="gl-toolbar">
            <input type="date" className="gl-date" value={date} onChange={e => setDate(e.target.value)} />
            <span style={{fontSize:'.78rem',color:'#9094a8'}}>{fmtDate(date)}</span>
          </div>

          <div className="stat-grid">
            <div className="stat-card">
              <div className="sc-icon green"><CalendarDaysIcon /></div>
              <div className="sc-label">Total Entries</div>
              <div className="sc-value">{total}</div>
            </div>
            <div className="stat-card">
              <div className="sc-icon amber"><ClockIcon /></div>
              <div className="sc-label">First Entry</div>
              <div className="sc-value" style={{fontSize:'1rem'}}>{firstEntry}</div>
            </div>
          </div>

          <div className="panel">
            <div className="panel-head">
              <div>
                <div className="panel-title">Entry Log — {fmtDate(date)}</div>
                <div className="panel-sub">{loading ? 'Loading…' : `${total} entr${total===1?'y':'ies'}`}</div>
              </div>
            </div>

            {loading ? (
              <div className="gl-loading"><div className="spinner"/>Loading…</div>
            ) : entries.length === 0 ? (
              <div className="gl-empty">No gate entries for this date.</div>
            ) : (
              <>
                <div className="tbl-wrap" style={{overflowX:'auto'}}>
                  <table className="tbl">
                    <thead><tr><th>#</th><th>Entry Time</th><th>Card UID</th><th>Source</th></tr></thead>
                    <tbody>
                      {entries.map((e, i) => (
                        <tr key={e._id}>
                          <td style={{color:'#c0c5dc',fontSize:'.72rem'}}>{i+1}</td>
                          <td><span className="time-badge"><ClockIcon />{fmtTime(e.entryTime)}</span></td>
                          <td><span className="mono">{e.cardUID}</span></td>
                          <td><span className={`source-badge ${e.source}`}>{e.source}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="cards">
                  {entries.map(e => (
                    <div key={e._id} className="card">
                      <div className="card-row"><span className="card-lbl">Entry Time</span><span className="time-badge"><ClockIcon />{fmtTime(e.entryTime)}</span></div>
                      <div className="card-row"><span className="card-lbl">Card UID</span><span className="mono">{e.cardUID}</span></div>
                      <div className="card-row"><span className="card-lbl">Source</span><span className={`source-badge ${e.source}`}>{e.source}</span></div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
